import React, { ReactElement, useEffect, useState } from 'react';
import { SelectProps, Space } from 'antd';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { App } from 'common/components/antd/App';
import { Select } from 'common/components/antd/Select/Select';
import { Table } from 'common/components/antd/Table/Table';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_ADMIN_FE } from 'common/constants/general-access-role.constants';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'common/constants/table.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { useGetBranchesMultiRoleQuery_MultiRole } from 'domains/branches/queries/branch.query';
import {
  TCounterpartyAvailableBranchesResData,
  TCounterpartyBranchesResData,
} from 'domains/branches/types/branch.types';
import { useGetCounterpartiesQuery } from 'domains/counterparties/queries/counterparty.query';
import { TScopeResData } from 'domains/roles/types/roles.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { getColumns } from './branches-table.columns';
import {
  EBranchesColumnKeys,
  EModalType,
  EUserManagementBranchesPageGetParams,
  EUserManagementBranchesPageGetParamsActionTypes,
} from './enums/branches.enum';
import CreateEditBranchModal from './modals/create-edit-branch.modal';
import {
  TBranchesTableRecord,
  TTablePaginationConfig,
  TUserManagementBranchesPageLoaderData,
  TBranchesModalData,
} from './types/branches.types';

const UserManagementBranchesPage = (): ReactElement => {
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const [selectedCounterparty, setSelectedCounterparty] = useState(ENV_COUNTERPARTY_GUID);
  const [paginationData, setPaginationData] = useState<TTablePaginationConfig>({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const loaderData = useLoaderData() as TUserManagementBranchesPageLoaderData;
  const [modalData, setModalData] = useState<TBranchesModalData | {}>({});

  const {
    data: counterpartiesData,
    isError: isCounterpartiesDataError,
    error: counterpartiesDataError,
  } = useGetCounterpartiesQuery();

  const counterpartyOptions = (counterpartiesData?.counterparties || []).map((c) => ({
    label: c.name,
    value: c.guid,
  }));

  const selectedCounterpartyOption = counterpartyOptions.find((opt) => opt.value === selectedCounterparty);

  const changeCounterparty: SelectProps['onChange'] = (value) => {
    setSelectedCounterparty(value);
  };

  const {
    data: branchesData,
    isError: isBranchesDataError,
    error: branchesDataError,
  } = useGetBranchesMultiRoleQuery_MultiRole(selectedCounterparty, {}, loaderData.hasAccessForAllBranches, {
    refetchOnMount: false,
    select: (data: any) => {
      const redoData = {
        ...data,
        ...(loaderData.hasAccessForAllBranches ? { branches: data.list } : null),
      };

      return redoData;
    },
  });

  const isError = isCounterpartiesDataError || isBranchesDataError;
  const error = counterpartiesDataError || branchesDataError;

  useEffect(() => {
    if (isError) {
      notification.error({
        message: (error as { message: string }).message,
        type: 'error',
      });
    }
  }, [isError, error]);

  const branchesTableData: TBranchesTableRecord[] | undefined = (
    (branchesData as TCounterpartyAvailableBranchesResData)?.branches || []
  ).map(
    (item): TBranchesTableRecord => ({
      name: item.name,
      guid: item.guid,
      counterparty_guid: '',
      created_at: '',
      is_deleted: false,
      updated_at: '',
    }),
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(document.location.search);
    const pageAction = searchParams.get(EUserManagementBranchesPageGetParams.Action);

    if (pageAction === EUserManagementBranchesPageGetParamsActionTypes.Create) {
      setModalData({
        type: EModalType.Create,
        rowData: branchesTableData,
        branchesData,
        selectedCounterparty: selectedCounterpartyOption,
        counterpartiesData,
      });
    }
  }, [document.location.search]);

  const columns = getColumns();

  const handlePaginationChange = (pageParam: number, pageSizeParam: number): void => {
    setPaginationData({
      page: pageParam,
      pageSize: pageSizeParam,
    });
  };

  const onCancelModalHandler = (): void=> {
    setModalData({});
    navigate(ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_BRANCHES);
  };

  return (
    <>
      <Space style={{ padding: '16px' }}>
        {IS_ADMIN_FE && (
          <Select
            style={{
              width: 272,
            }}
            defaultValue={selectedCounterparty}
            value={selectedCounterpartyOption}
            options={counterpartyOptions}
            onChange={changeCounterparty}
          />
        )}
      </Space>
      <Table
        pagination={{
          style: {
            marginRight: 30,
          },
          showQuickJumper: true,
          showSizeChanger: true,
          total: branchesData?.total || 0,
          pageSize: paginationData.pageSize,
          current: paginationData.page,
          onChange: handlePaginationChange,
        }}
        rowKey={EBranchesColumnKeys.Guid}
        dataSource={branchesTableData}
        columns={columns}
      />
      <CreateEditBranchModal
        isOpen={!!Object.keys(modalData).length}
        onCancelModalHandler={onCancelModalHandler}
        data={modalData as TBranchesModalData}
      />
    </>
  );
};

export const loader = async (): Promise<{
  branchesData: TCounterpartyBranchesResData | TCounterpartyAvailableBranchesResData | {},
  hasAccessForAllBranches: boolean,
}> => {
  try {
    const scopeListDataQuery =
      queryKeysClient[EQueryKeys.RbacQueryKeys].getCounterpartyScopesList(ENV_COUNTERPARTY_GUID);
    const scopeListData: TScopeResData =
      queryClient.getQueryData(scopeListDataQuery.queryKey) ??
      (await queryClient.fetchQuery(scopeListDataQuery));

    const hasAccessForAllBranches =
      (scopeListData as TScopeResData).scopes.find(({ scope }) => scope === 'branches')?.is_allowed || false;

    const getBranchesMultiRoleQuery = queryKeysClient[EQueryKeys.BranchQueryKeys].getBranches_MultiRole(
      ENV_COUNTERPARTY_GUID,
      {},
      hasAccessForAllBranches,
    );
    const branchesData: TCounterpartyBranchesResData | TCounterpartyAvailableBranchesResData =
      queryClient.getQueryData(getBranchesMultiRoleQuery.queryKey) ??
      (await queryClient.fetchQuery(getBranchesMultiRoleQuery));

    return {
      branchesData,
      hasAccessForAllBranches,
    };
  } catch (error) {
    console.error(error);

    return {
      branchesData: {},
      hasAccessForAllBranches: false,
    };
  }
};

export default UserManagementBranchesPage;
